import React, { useState } from 'react';
import { Modal } from "../../../components/ui/modal";
import Button from "../../../components/ui/button/Button";
import Input from "../../../components/form/input/InputField";
import Select from "../../../components/form/Select";
import { Calendar, DollarSign, FileText, ShoppingBag, CreditCard, Truck, ClipboardList } from 'lucide-react';
import useApi from '../../../hooks/useApi';
import { toast } from 'react-hot-toast';

interface CreateGastoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

interface FormErrors {
  fecha?: string;
  monto?: string;
  proveedor?: string;
  descripcion?: string;
  categoria?: string;
}

const CreateGastoModal: React.FC<CreateGastoModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const api = useApi();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  
  // Form state
  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0], // Current date as default
    monto: '',
    proveedor: '',
    descripcion: '',
    categoria: '',
    metodoPago: 'efectivo',
    comprobante: '',
    notas: ''
  });

  const [categorias] = useState([
    { value: 'transporte', label: 'Transporte' },
    { value: 'suministros', label: 'Suministros' },
    { value: 'comisiones', label: 'Comisiones' },
    { value: 'servicios', label: 'Servicios' },
    { value: 'equipos', label: 'Equipos' },
    { value: 'alquiler', label: 'Alquiler' },
    { value: 'otros', label: 'Otros' }
  ]);

  const [metodosPago] = useState([
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'transferencia', label: 'Transferencia bancaria' },
    { value: 'tarjeta', label: 'Tarjeta' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'otro', label: 'Otro' }
  ]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.fecha) {
      newErrors.fecha = 'Por favor, seleccione una fecha válida';
    }
    
    if (!formData.monto || parseFloat(formData.monto) <= 0) {
      newErrors.monto = 'Ingrese un monto válido mayor a 0';
    }
    
    if (!formData.descripcion.trim()) {
      newErrors.descripcion = 'La descripción es requerida';
    }
    
    if (!formData.categoria) {
      newErrors.categoria = 'Seleccione una categoría';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value: string, name: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create the gasto object
      const gastoData = {
        fecha: formData.fecha,
        monto: parseFloat(formData.monto),
        proveedor: formData.proveedor,
        descripcion: formData.descripcion,
        categoria: formData.categoria,
        metodoPago: formData.metodoPago,
        comprobante: formData.comprobante || undefined,
        notas: formData.notas || undefined
      };
      
      // Call the API to create the gasto
      await api.createGasto(gastoData);
      
      toast.success('Gasto registrado exitosamente');
      
      // Reset form and close modal
      setFormData({
        fecha: new Date().toISOString().split('T')[0],
        monto: '',
        proveedor: '',
        descripcion: '',
        categoria: '',
        metodoPago: 'efectivo',
        comprobante: '',
        notas: ''
      });
      
      // Refresh data
      if (onSuccess) {
        await onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Error al registrar gasto:', error);
      toast.error('Error al registrar el gasto. Por favor, inténtelo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Registrar nuevo gasto">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Fecha del gasto */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              <Calendar className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
              Fecha
              <span className="text-red-500">*</span>
            </label>
            <Input
              type="date"
              name="fecha"
              value={formData.fecha}
              onChange={handleInputChange}
              onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.showPicker()}
              placeholder="Seleccionar fecha"
              className="w-full"
              error={errors.fecha ? true : false}
              hint={errors.fecha}
            />
          </div>

          {/* Monto del gasto */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              <DollarSign className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
              Monto
              <span className="text-red-500">*</span>
            </label>
            <Input
              type="number"
              name="monto"
              value={formData.monto}
              onChange={handleInputChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              error={errors.monto ? true : false}
              hint={errors.monto}
              className="w-full"
            />
          </div>
        </div>

        {/* Proveedor */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <Truck className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
            Proveedor
          </label>
          <Input
            type="text"
            name="proveedor"
            value={formData.proveedor}
            onChange={handleInputChange}
            placeholder="Nombre del proveedor"
            error={errors.proveedor ? true : false}
            hint={errors.proveedor}
            className="w-full"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Especifique el proveedor de este gasto (empresa, persona, etc.)
          </p>
        </div>

        {/* Descripción */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <ShoppingBag className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
            Descripción
            <span className="text-red-500">*</span>
          </label>
          <textarea
            name="descripcion"
            value={formData.descripcion}
            onChange={handleInputChange}
            placeholder="Descripción detallada del gasto"
            rows={3}
            className={`w-full px-3 py-2 border rounded-md ${
              errors.descripcion 
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
            } dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
          />
          {errors.descripcion && (
            <p className="text-xs text-red-500">{errors.descripcion}</p>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Detalle el propósito o contexto de este gasto
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Categoría */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              <FileText className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
              Categoría
              <span className="text-red-500">*</span>
            </label>
            <Select
              options={categorias}
              defaultValue={formData.categoria}
              onChange={(value) => handleSelectChange(value, 'categoria')}
              placeholder="Seleccionar categoría"
              className="w-full"
            />
            {errors.categoria && (
              <p className="text-xs text-red-500">{errors.categoria}</p>
            )}
          </div>

          {/* Método de pago */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              <CreditCard className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
              Método de Pago
            </label>
            <Select
              options={metodosPago}
              defaultValue={formData.metodoPago}
              onChange={(value) => handleSelectChange(value, 'metodoPago')}
              placeholder="Seleccionar método"
              className="w-full"
            />
          </div>
        </div>

        {/* Número de comprobante */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <FileText className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
            Número de Comprobante
          </label>
          <Input
            type="text"
            name="comprobante"
            value={formData.comprobante}
            onChange={handleInputChange}
            placeholder="Referencia de factura o ticket"
            className="w-full"
          />
        </div>

        {/* Notas adicionales */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <ClipboardList className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
            Notas Adicionales
          </label>
          <textarea
            name="notas"
            value={formData.notas}
            onChange={handleInputChange}
            placeholder="Información adicional sobre el gasto"
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="px-4"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="px-4"
          >
            {isSubmitting ? 'Registrando...' : 'Registrar Gasto'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateGastoModal; 